<template>
  <div
    class="c_search_widget c_box"
    :class="{
      'c_box--black': navigation,
      'c_search_widget--hidden': searchHidden,
      'c_search_widget--visible': getSearchState && searchHidden,
    }"
  >
    <form
      :action="searchAction ? `${searchAction}` : null"
      :method="searchAction ? 'GET' : null"
      class="c_search_widget__form c_wrapper"
      @submit.prevent="onSubmit"
    >
      <div class="c_search_widget__hidden">
        <label :for="honeypotName">{{ $t('Honeypot') }}</label>
        <input type="text" :id="honeypotName" :name="honeypotName" v-model="honeypot" tabindex="-1" ref="honeypot" />
      </div>
      <div class="c_search_widget__content">
        <label :for="searchInputId" class="c_search_widget__label">{{
          getGlobalCopy?.[0]?.searchFormLabel ?? $t('Search')
        }}</label>
        <input
          type="search"
          name="q"
          :id="searchInputId"
          :placeholder="getGlobalCopy?.[0]?.searchFormPlaceholder ?? $t('Search Placeholder')"
          v-model="query"
          required
          class="c_search_widget__input"
          ref="searchInput"
          v-focus="getSearchState"
        />
      </div>
      <button v-if="!searchAction" type="submit" class="c_search_widget__submit c_button">
        <IconSVG
          tag="span"
          class="c_search_widget__icon"
          stroke-color-var="white"
          :height="'25'"
          :width="'25'"
          stroke-width="4px"
          handle="search"
          aria-hidden="true"
          :replacements="{ Search: $t('Search') }"
        />
        <span class="c_search_widget__submit__label">{{
          getGlobalCopy?.[0]?.searchFormButtonLabel ?? $t('Search')
        }}</span>
      </button>
      <button v-else type="submit" class="c_search_widget__submit c_button">
        <IconSVG
          tag="span"
          class="c_search_widget__icon"
          stroke-color-var="white"
          :height="'25'"
          :width="'25'"
          stroke-width="4px"
          handle="search"
          aria-hidden="true"
          :replacements="{ Search: $t('Search') }"
        />
        <span class="c_search_widget__submit__label">{{
          getGlobalCopy?.[0]?.searchFormButtonLabel ?? $t('Search')
        }}</span>
      </button>
    </form>
    <div role="region" id="search-results" aria-live="polite" class="c_search_widget__results">
      <div
        class="c_search_widget__results_success"
        v-if="pageResults && pageResults.length > 0"
        :data-insights-index="determineIndex"
      >
        <SearchResult
          v-for="(item, index) in pageResults"
          :key="index"
          :name="item.name"
          :url="item.url"
          :snippet="item.snippet"
          :snippet-field="item.snippetField"
          :object-id="item.objectID"
          :position="item.position"
          :query-id="item.queryID"
          :index-name="item.index"
          :gtm-click-delay-ms="resultClickDelayMs"
          :gtm-use-router-navigation="resultClickUseRouter"
          @result-click="pushAlgoliaClickedEvent"
        />
        <Flex class="c_search_widget__pagination" v-if="pageResults.length > 0 && pages > 1 && query">
          <button
            v-show="currentPage > 1"
            aria-controls="search-results"
            :aria-label="$t('First page')"
            @click="paginate('first')"
            class="c_search_widget__pagination__indicator"
          >
            «
          </button>
          <button
            v-show="currentPage > 1"
            aria-controls="search-results"
            :aria-label="$t('Previous page')"
            @click="paginate('previous')"
            class="c_search_widget__pagination__indicator"
          >
            &lt;
          </button>
          <template v-for="(page, index) in paginationList">
            <button
              v-if="page !== '...'"
              :key="`page-${page}`"
              :aria-label="$t('Search page') + ` ${page}`"
              @click="paginate('single', null, page)"
              :class="{
                c_search_widget__pagination__indicator: true,
                'c_search_widget__pagination__indicator--current': currentPage === page,
              }"
            >
              {{ page }}
            </button>
            <span v-else :key="`ellipsis-${index}`" class="c_search_widget__pagination__ellipsis"> … </span>
          </template>
          <button
            v-show="currentPage < pages"
            aria-controls="search-results"
            :aria-label="$t('Next page')"
            @click="paginate('next')"
            class="c_search_widget__pagination__indicator"
          >
            &gt;
          </button>
          <button
            v-show="currentPage < pages"
            aria-controls="search-results"
            :aria-label="$t('Last page')"
            @click="paginate('last')"
            class="c_search_widget__pagination__indicator"
          >
            »
          </button>
        </Flex>
      </div>
      <div v-else-if="loading" class="c_search_widget__loading">
        <span>{{ $t('Loading results...') }}</span>
      </div>
      <div
        v-else-if="query && Array.isArray(pageResults) && pageResults.length === 0 && !errorContentVisible && !loading"
        class="c_search_widget__no_results"
      >
        {{ $t('No results were returned.') }}
      </div>
      <div class="c_search_widget__results_error">
        <MatrixBlockSearchPageAfterContent v-show="errorContent && errorContentVisible" :matrix-blocks="errorContent" />
        <MatrixBlockSearchPageAfterContent
          v-show="noResultsContent && noResultsContentVisible"
          :matrix-blocks="noResultsContent"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { defineComponent } from '@nuxtjs/composition-api';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import SearchResult from 'Components/search/SearchResult.vue';
import Flex from 'Components/flex/Flex.vue';
import MatrixBlockSearchPageAfterContent from 'Components/matrix_blocks/MatrixBlockSearchPageAfterContent.vue';

const Snippet_Fields = [
  'content',
  'blogContent',
  'bodyContent2022',
  'relatedEntryDescription',
  'description',
  'landingPageContent2023',
  'landingPageContent',
  'blogContent',
  'articleSummary',
  'relatedProductDescription',
  'relatedProductCategories',
  'productFilters',
];

export default defineComponent({
  name: 'SearchWidget',
  components: {
    Flex,
    IconSVG,
    SearchResult,
    MatrixBlockSearchPageAfterContent,
  },
  data() {
    return {
      totalPages: 1,
      honeypot: '',
      searchResponse: null,
      query: '',
      previousQuery: '',
      FromLocalStore: false,
      currentPage: 1,
      defaultOffsetInterval: 10,
      currentOffsetStart: 0,
      currentOffsetEnd: 10,
      currentOffsetInterval: 10,
      errorContentVisible: false,
      noResultsContentVisible: false,
      algoliaBaseUrl: `https://${process.env.ALGOLIA_APP_ID}.algolia.net/1/indexes`,
      loading: false,
    };
  },
  props: {
    navigation: { type: Boolean, required: false, default: false },
    searchAction: { type: String, required: false, default: null },
    searchInputId: { type: String, required: true },
    searchHidden: { type: Boolean, required: false, default: false },
    searchResultsVisible: { type: Boolean, required: false, default: true },
    searchCount: { type: Number, required: false, default: 50 },
    errorContent: { type: Array, required: false, default: () => [] },
    noResultsContent: { type: Array, required: false, default: () => [] },
    gtmViewedEventName: { type: String, required: false, default: 'Hits Viewed' },
    gtmClickedEventName: { type: String, required: false, default: 'Result Clicked' },
    gtmViewedAaEventName: { type: String, required: false, default: 'Results Viewed' },
    gtmClickedAaEventName: { type: String, required: false, default: 'Result Clicked' },
    gtmPushEnabled: { type: Boolean, required: false, default: true },
    gtmIncludePrefixedKeys: { type: Boolean, required: false, default: true },
    resultClickDelayMs: { type: Number, required: false, default: 0 },
    resultClickUseRouter: { type: Boolean, required: false, default: false },
  },
  setup() {},
  computed: {
    getGlobalCopy() {
      return this.$config?.craftGlobals?.search?.filter((item) => item.language === this.$i18n.locale);
    },
    getSearchState() {
      return this.$store?.state?.global?.searchOpen;
    },
    honeypotName() {
      return `fuzzy-${Math.floor(Math.random() * 1000)}`;
    },
    validateHoneypot() {
      return !(this.honeypot !== '' || this.$refs.honeypot?.value !== '');
    },
    searchMarket() {
      switch (this.$i18n.locale) {
        case 'ja':
          return 'ja-JP';
        case 'ko':
          return 'ko-KR';
        case 'zh':
          return 'zh-CN';
        default:
          return 'en-US';
      }
    },
    pageResults() {
      if (this.searchResponse && Array.isArray(this.searchResponse.value)) {
        return this.searchResponse.value;
      }
      return [];
    },
    pages() {
      return this.totalPages || 1;
    },
    determineIndex() {
      const locale = this.$i18n?.locale?.toUpperCase() || 'EN';
      const environment = process.env.DEPLOYED_ENV || 'local';
      return `${environment}_index_${locale}`;
    },
    paginationList() {
      const total = this.pages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const left = Math.max(2, current - delta);
      const right = Math.min(total - 1, current + delta);
      range.push(1);
      if (left > 2) range.push('...');
      for (let i = left; i <= right; i++) {
        range.push(i);
      }
      if (right < total - 1) range.push('...');
      if (total > 1) range.push(total);
      return range;
    },
  },
  directives: {
    focus: {
      update(el, binding) {
        if (binding.value) el.focus();
      },
    },
  },
  watch: {
    query(input) {
      if (!this.searchAction) {
        this.query = input;
      }
    },
    honeypot(val) {
      this.honeypot = val;
    },
  },
  methods: {
    hasOneTrustCookie(name) {
      if (typeof document === 'undefined') return false;
      const escapedName = String(name).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
      return new RegExp('(?:^|; )' + escapedName + '=').test(document.cookie);
    },
    hasOneTrustDecision() {
      return this.hasOneTrustCookie('OptanonConsent') || this.hasOneTrustCookie('OptanonAlertBoxClosed');
    },
    showOneTrustBanner() {
      if (typeof window === 'undefined') return;
      if (typeof document !== 'undefined') {
        if (document.getElementById('onetrust-banner-sdk') || document.querySelector('.ot-sdk-container')) {
          return;
        }
      }
      const ot = window.OneTrust;
      if (!ot) return;

      try {
        if (typeof ot.LoadBanner === 'function') {
          ot.LoadBanner();
          return;
        }
      } catch (_e) {
        // ignore
      }

      try {
        if (typeof ot.ToggleInfoDisplay === 'function') {
          ot.ToggleInfoDisplay();
        }
      } catch (_e) {
        // ignore
      }
    },
    showOneTrustBannerIfNoDecision() {
      if (typeof window === 'undefined') return;
      if (this.hasOneTrustDecision()) return;

      if (window.__kodakOneTrustBannerPrompted) return;
      window.__kodakOneTrustBannerPrompted = true;

      const self = this;
      const tryShow = () => {
        if (window.OneTrust) {
          self.showOneTrustBanner();
          return true;
        }
        return false;
      };

      if (tryShow()) return;

      const previousWrapper = window.OptanonWrapper;
      window.OptanonWrapper = function () {
        if (typeof previousWrapper === 'function') {
          try {
            previousWrapper();
          } catch (_e) {
            // ignore
          }
        }
        tryShow();
      };

      let attempts = 0;
      const interval = setInterval(() => {
        attempts += 1;
        if (tryShow() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 250);
    },
    isSearchAnalyticsAllowed() {
      try {
        if (typeof window === 'undefined' || typeof document === 'undefined') return false;

        const getCookie = (name) => {
          const escapedName = name.replace(/([.$?*|{}()\]/^])/g, '\\$1');
          const pattern = new RegExp('(?:^|; )' + escapedName + '=([^;]*)');
          const match = document.cookie.match(pattern);
          return match ? decodeURIComponent(match[1]) : null;
        };

        const optanon = getCookie('OptanonConsent');
        if (!optanon) {
          return false;
        }

        const groupsMatch = optanon.match(/groups=([^&]+)/);
        if (!groupsMatch || !groupsMatch[1]) return false;

        const groupsStr = groupsMatch[1];
        const groups = groupsStr.split(',');
        const ANALYTICS_GROUP_ID = 'C0002';

        let analyticsAllowed = false;
        for (const g of groups) {
          const [id, val] = g.split(':');
          if (id === ANALYTICS_GROUP_ID) {
            analyticsAllowed = val === '1';
            break;
          }
        }

        return !!analyticsAllowed;
      } catch (_e) {
        return false;
      }
    },
    ensureUserToken() {
      try {
        if (typeof window === 'undefined') return null;
        const KEY = 'algoliaUserToken';
        let token = localStorage.getItem(KEY);

        if (!token) {
          if (!this.isSearchAnalyticsAllowed()) {
            return null;
          }

          const rand = (n = 16) =>
            window.crypto && window.crypto.getRandomValues
              ? Array.from(window.crypto.getRandomValues(new Uint8Array(n)))
                  .map((b) => b.toString(16).padStart(2, '0'))
                  .join('')
              : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
          token = `anon_${rand(16)}`;
          localStorage.setItem(KEY, token);
        }
        window.algoliaUserToken = token;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ algoliaUserToken: token });
        return token;
      } catch (_e) {
        return null;
      }
    },
    getAlgoliaUserToken() {
      if (typeof window === 'undefined') return null;
      return window.algoliaUserToken || localStorage.getItem('algoliaUserToken') || null;
    },
    escapeHtml(s) {
      return (s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    stripTags(s) {
      return s ? String(s).replace(/<\/?[^>]+(>|$)/g, '') : '';
    },
    normalizeEntities(s) {
      return s
        ? String(s)
            .replace(/&(nbsp|#160);/gi, ' ')
            .replace(/\u00A0/g, ' ')
        : '';
    },
    collapseWhitespace(s) {
      return s ? String(s).replace(/\s+/g, ' ').trim() : '';
    },
    tidyEllipsis(s) {
      return s
        ? String(s)
            .replace(/\s*…\s*/g, '…')
            .replace(/^…\s+/, '…')
            .replace(/\s{2,}/g, ' ')
            .trim()
        : s;
    },
    tokenizeQuery(q) {
      const terms =
        (q || '')
          .toString()
          .toLowerCase()
          .match(/[a-z0-9]+/g) || [];
      const seen = new Set();
      const filtered = [];
      for (const t of terms) {
        if (t.length < 2) continue;
        if (!seen.has(t)) {
          seen.add(t);
          filtered.push(t);
        }
      }
      return filtered;
    },
    buildHighlightRegex(terms) {
      if (!terms || !terms.length) return null;
      const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = terms.map(esc).join('|');
      try {
        return new RegExp(`(${pattern})`, 'gi');
      } catch (_e) {
        return null;
      }
    },
    highlightTextSafe(text, terms) {
      const plain = this.collapseWhitespace(this.normalizeEntities(this.stripTags(text || '')));
      if (!plain) return '';
      const re = this.buildHighlightRegex(terms);
      if (!re) return this.escapeHtml(plain);
      const marked = plain.replace(re, '[[H]]$1[[/H]]');
      const escaped = this.escapeHtml(marked);
      return escaped.replace(/\[\[H\]\]/g, '<b>').replace(/\[\[\/H\]\]/g, '</b>');
    },
    makeContextSnippet(text, terms, maxLen = 220) {
      const plain = this.collapseWhitespace(this.normalizeEntities(this.stripTags(text || '')));
      if (!plain) return '';
      const re = this.buildHighlightRegex(terms);
      if (!re) return plain.length > maxLen ? plain.slice(0, maxLen).replace(/^(.*)\b.*/, '$1') + '…' : plain;
      const m = re.exec(plain);
      if (!m) return plain.length > maxLen ? plain.slice(0, maxLen).replace(/^(.*)\b.*/, '$1') + '…' : plain;
      const idx = m.index;
      const half = Math.floor(maxLen / 2);
      let start = Math.max(0, idx - half);
      let end = Math.min(plain.length, start + maxLen);
      const pre = plain.lastIndexOf(' ', start + 1);
      if (pre > 20) start = pre + 1;
      const nxt = plain.indexOf(' ', end - 1);
      if (nxt !== -1) end = Math.min(nxt, plain.length);
      let snippet = plain.slice(start, end).trim();
      if (start > 0) snippet = '… ' + snippet;
      if (end < plain.length) snippet = snippet + ' …';
      return snippet;
    },
    getFallbackTitle(hit) {
      const h = hit || {};
      return h.title ?? h.name ?? 'No Title';
    },
    shouldUseAlgoliaHighlightedTitle(hit) {
      const h = hit || {};
      const titleHLValue = h._highlightResult?.title?.value || h._highlightResult?.name?.value || null;
      const titleMatchLevel = h._highlightResult?.title?.matchLevel || h._highlightResult?.name?.matchLevel || 'none';
      const titleHasTags = /<[^>]+>/.test(titleHLValue || '');
      return !!(titleHasTags && titleMatchLevel !== 'none');
    },
    getHitUrl(hit) {
      const h = hit || {};
      const url = h.url ?? h.link ?? '#';
      return typeof url === 'string' && url.length ? url : '#';
    },
    isNonEmptyString(s) {
      return typeof s === 'string' && s.trim().length > 0;
    },
    selectPreferredSnippetWithField(snippetResultObj, preferMatched = true) {
      if (!snippetResultObj || typeof snippetResultObj !== 'object') return { snippetObj: null, fieldName: null };
      const contentSnippet = snippetResultObj['content'];
      if (
        contentSnippet &&
        this.isNonEmptyString(contentSnippet.value) &&
        contentSnippet.matchLevel &&
        contentSnippet.matchLevel !== 'none'
      ) {
        return { snippetObj: contentSnippet, fieldName: 'content' };
      }
      for (const [field, val] of Object.entries(snippetResultObj)) {
        if (
          field !== 'content' &&
          val &&
          this.isNonEmptyString(val.value) &&
          val.matchLevel &&
          val.matchLevel !== 'none'
        ) {
          return { snippetObj: val, fieldName: field };
        }
      }
      const entries = Object.entries(snippetResultObj).filter(
        ([field, val]) => val && this.isNonEmptyString(val.value)
      );
      if (entries.length) {
        const [fieldName, snippetObj] = entries[0];
        return { snippetObj, fieldName };
      }
      return { snippetObj: null, fieldName: null };
    },
    getRawFallbackContent(hit) {
      const h = hit || {};
      const candidates = Snippet_Fields.map((field) => h[field]);
      const shownContent = candidates.find((v) => this.isNonEmptyString(v));
      return shownContent || '';
    },
    buildSnippetWithField(hit, terms) {
      if (!hit) return { snippet: '', snippetField: null };

      const snippetResultObj = hit._snippetResult || {};
      const { snippetObj, fieldName } = this.selectPreferredSnippetWithField(snippetResultObj, true);

      const algoliaSnippetHtml = snippetObj?.value || '';
      const snippetMatchLevel = snippetObj?.matchLevel || 'none';
      const snippetHasTags = /<[^>]+>/.test(algoliaSnippetHtml || '');

      const rawFallback = this.getRawFallbackContent(hit);

      let snippet = '';
      if (this.isNonEmptyString(algoliaSnippetHtml) && (snippetHasTags || snippetMatchLevel !== 'none')) {
        snippet = this.tidyEllipsis(this.collapseWhitespace(this.normalizeEntities(algoliaSnippetHtml)));
      } else {
        const context = this.makeContextSnippet(rawFallback, terms, 220);
        const highlighted = this.highlightTextSafe(context, terms);
        snippet = this.tidyEllipsis(highlighted);
        if (!snippet) {
          const normalized = this.collapseWhitespace(this.normalizeEntities(this.stripTags(rawFallback)));
          snippet = normalized ? (normalized.length > 400 ? normalized.slice(0, 400) + '…' : normalized) : '';
        }
      }
      return { snippet, snippetField: fieldName };
    },
    transformHit(hit, terms) {
      if (!hit || typeof hit !== 'object') return { name: 'No Title', url: '#', snippet: '', snippetField: null };
      const fallbackTitle = this.getFallbackTitle(hit);
      const titleHLValue = hit._highlightResult?.title?.value || hit._highlightResult?.name?.value || null;
      const name = this.shouldUseAlgoliaHighlightedTitle(hit)
        ? titleHLValue || this.escapeHtml(String(fallbackTitle))
        : this.highlightTextSafe(fallbackTitle, terms);

      const url = this.getHitUrl(hit);
      const { snippet, snippetField } = this.buildSnippetWithField(hit, terms);

      return { name, url, snippet, snippetField };
    },
    normalizePath(p) {
      const s = typeof p === 'string' ? p : '';
      const norm = s.replace(/\/+/g, '/').replace(/\/$/, '');
      return norm || '/';
    },
    isSameRoute(a, b) {
      return this.normalizePath(a) === this.normalizePath(b);
    },
    pushToDataLayer(payload) {
      try {
        if (typeof window === 'undefined' || !this.gtmPushEnabled) return;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        if (this.$gtm && typeof this.$gtm.push === 'function') {
          this.$gtm.push(payload);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('SearchWidget: dataLayer push failed', e);
      }
    },
    pushAlgoliaViewedEvent(index, query, queryID, items) {
      try {
        if (!items || !items.length) return;
        const objectIDs = items.map((i) => i.objectID).filter(Boolean);
        const positions = items.map((i) => i.position).filter((p) => p !== undefined && p !== null);
        const basePayload = {
          event: this.gtmViewedEventName || 'Hits Viewed',
          index,
          query,
          queryID: queryID || null,
          objectIDs,
          positions,
          aaEventName: this.gtmViewedAaEventName || 'Results Viewed',
        };
        const payload = this.gtmIncludePrefixedKeys
          ? {
              ...basePayload,
              algolia_index: index,
              algolia_query: query,
              algolia_queryID: queryID || null,
              algolia_objectIDs: objectIDs,
              algolia_positions: positions,
              algolia_aaEventName: this.gtmViewedAaEventName || 'Results Viewed',
            }
          : basePayload;
        this.pushToDataLayer(payload);
        if ((this.gtmViewedEventName || 'Hits Viewed') !== 'Hits Viewed') {
          this.pushToDataLayer({ event: 'Hits Viewed' });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('SearchWidget: pushAlgoliaViewedEvent failed', e);
      }
    },
    pushAlgoliaClickedEvent(payload) {
      try {
        const emitted = payload || {};
        const emittedObjectID = emitted.objectID ?? emitted.objectId ?? null;
        let emittedQueryID = emitted.queryID ?? emitted.queryId ?? null;
        let emittedIndex = emitted.index ?? emitted.indexName ?? null;
        let emittedPosition =
          typeof emitted.position === 'number'
            ? emitted.position
            : typeof emitted.pos === 'number'
              ? emitted.pos
              : null;
        const url = emitted.url || null;
        let inferred = null;
        if (
          (!emittedQueryID || !emittedIndex || emittedPosition == null) &&
          emittedObjectID &&
          Array.isArray(this.searchResponse?.value)
        ) {
          inferred = this.searchResponse.value.find((i) => i && i.objectID === emittedObjectID) || null;
          if (inferred) {
            emittedQueryID = emittedQueryID ?? inferred.queryID ?? inferred.queryId ?? null;
            emittedIndex = emittedIndex ?? inferred.index ?? null;
            emittedPosition =
              emittedPosition != null
                ? emittedPosition
                : typeof inferred.position === 'number'
                  ? inferred.position
                  : null;
          }
        }
        const basePayload = {
          event: this.gtmClickedEventName || 'Result Clicked',
          index: emittedIndex || this.determineIndex,
          queryID: emittedQueryID || null,
          objectIDs: emittedObjectID ? [emittedObjectID] : [],
          positions: emittedPosition != null ? [emittedPosition] : [],
          url,
          aaEventName: this.gtmClickedAaEventName || 'Result Clicked',
        };
        const withDup = this.gtmIncludePrefixedKeys
          ? {
              ...basePayload,
              algolia_index: basePayload.index,
              algolia_queryID: basePayload.queryID,
              algolia_objectIDs: basePayload.objectIDs,
              algolia_positions: basePayload.positions,
              algolia_url: basePayload.url,
              algolia_aaEventName: this.gtmClickedAaEventName || 'Result Clicked',
            }
          : basePayload;
        this.pushToDataLayer(withDup);
        try {
          if (typeof window !== 'undefined' && basePayload.objectIDs.length) {
            const record = {
              index: basePayload.index || null,
              queryID: basePayload.queryID || null,
              objectIDs: basePayload.objectIDs || [],
              positions: basePayload.positions || [],
              url: basePayload.url || null,
              ts: Date.now(),
            };
            sessionStorage.setItem('AlgoliaLastSearchClick', JSON.stringify(record));
          }
        } catch (_persistErr) {
          // nope
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('SearchWidget: pushAlgoliaClickedEvent failed', e);
      }
    },

    onSubmit(e) {
      if (this.searchAction) {
        this.sendQueryAsLocalStorage();
      } else {
        if (this.validateForm(e)) {
          this.sendQuery();
        }
      }
    },
    sessionStorageKey(indexName) {
      return `SearchQuery:${indexName}`;
    },
    saveSearchSession() {
      try {
        if (typeof window === 'undefined') return;
        const indexName = this.determineIndex;
        const payload = {
          q: this.query,
          page: this.currentPage,
          totalPages: this.totalPages,
          results: this.searchResponse?.value || [],
          ts: Date.now(),
          indexName,
        };
        sessionStorage.setItem(this.sessionStorageKey(indexName), JSON.stringify(payload));
      } catch (e) {
        console.warn('SearchWidget: saveSearchSession failed', e);
      }
    },
    restoreSearchSession() {
      try {
        if (typeof window === 'undefined') return false;
        const indexName = this.determineIndex;
        const raw = sessionStorage.getItem(this.sessionStorageKey(indexName));
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.results)) return false;
        if (!this.query && data.q) this.query = data.q;
        this.currentPage = data.page || 1;
        this.totalPages = data.totalPages || 1;
        this.searchResponse = { value: data.results || [] };
        this.setErrorMessageState(false, this.searchResponse.value.length === 0);
        return true;
      } catch (e) {
        console.warn('SearchWidget: restoreSearchSession failed', e);
        return false;
      }
    },
    clearSearchSession() {
      try {
        if (typeof window === 'undefined') return;
        const indexName = this.determineIndex;
        sessionStorage.removeItem(this.sessionStorageKey(indexName));
      } catch (e) {
        console.warn('SearchWidget: clearSearchSession failed', e);
      }
    },
    consumePersistedQuery() {
      if (typeof window === 'undefined') return;
      const q = localStorage.getItem('searchQuery');
      if (q && q.length) {
        this.FromLocalStore = true;
        this.query = q;
        this.sendQuery();
      }
    },
    disableHoneypotField() {
      if (this.$refs.honeypot) {
        this.$refs.honeypot.disabled = true;
      }
    },
    validateForm(e) {
      if (!this.validateHoneypot) {
        e.preventDefault();
        return false;
      }
      this.disableHoneypotField();
      return true;
    },
    setErrorMessageState(error = false, noResults = false) {
      this.errorContentVisible = error;
      this.noResultsContentVisible = noResults;
    },
    clearSearchResults() {
      this.searchResponse = {};
      this.currentPage = 1;
      this.currentOffsetStart = 0;
      this.currentOffsetEnd = this.defaultOffsetInterval;
      this.errorContentVisible = false;
      this.noResultsContentVisible = false;
    },
    async sendQuery() {
      if (!this.query) return;
      this.loading = true;
      const indexName = this.determineIndex;
      const url = `${this.algoliaBaseUrl}/${indexName}/query`;
      const headers = {
        'X-Algolia-API-Key': process.env.ALGOLIA_SEARCH_ONLY_API_KEY,
        'X-Algolia-Application-Id': process.env.ALGOLIA_APP_ID,
      };
      const snippetAttrs = Snippet_Fields.map((f) => `${f}:60`).join(',');
      const highlightAttrs = '*';
      const paramsArr = [
        `query=${encodeURIComponent(this.query)}`,
        `hitsPerPage=${this.currentOffsetInterval}`,
        `page=${this.currentPage - 1}`,
        `attributesToSnippet=${encodeURIComponent(snippetAttrs)}`,
        `attributesToHighlight=${encodeURIComponent(highlightAttrs)}`,
        `highlightPreTag=${encodeURIComponent('<b>')}`,
        `highlightPostTag=${encodeURIComponent('</b>')}`,
        `snippetEllipsisText=${encodeURIComponent('…')}`,
        'restrictHighlightAndSnippetArrays=false',
      ];
      if (this.isSearchAnalyticsAllowed()) {
        paramsArr.push('clickAnalytics=true');
      }
      const userToken = this.getAlgoliaUserToken() || this.ensureUserToken();
      if (userToken) paramsArr.push(`userToken=${encodeURIComponent(userToken)}`);
      const body = { params: paramsArr.join('&') };
      try {
        const response = await axios.post(url, body, { headers });
        const terms = this.tokenizeQuery(this.query);

        const hits = Array.isArray(response?.data?.hits) ? response.data.hits : [];
        const topQueryID = response?.data?.queryID || null;
        const items = hits.map((hit, i) => {
          const transformed = this.transformHit(hit, terms);
          const objectID = hit.objectID || hit.objectId || hit.id || null;
          const perHitQueryID = hit.__queryID || topQueryID || null;
          const position = (this.currentPage - 1) * this.currentOffsetInterval + (i + 1);
          return { ...transformed, objectID, position, queryID: perHitQueryID, index: indexName };
        });
        this.searchResponse = {
          value: items,
        };

        const nbPages = Number(response?.data?.nbPages);
        this.totalPages = Number.isFinite(nbPages) && nbPages > 0 ? nbPages : 1;

        this.saveSearchSession();

        this.pushAlgoliaViewedEvent(indexName, this.query, topQueryID, items);

        this.setErrorMessageState(false, (this.searchResponse.value || []).length === 0);
        if (this.FromLocalStore && typeof window !== 'undefined') {
          localStorage.removeItem('searchQuery');
          this.FromLocalStore = false;
        }
        this.loading = false;
      } catch (error) {
        console.error('Algolia search error:', error);
        if (this.FromLocalStore && typeof window !== 'undefined') {
          localStorage.removeItem('searchQuery');
          this.FromLocalStore = false;
        }
        this.setErrorMessageState(true, false);
        this.loading = false;
      }
    },
    sendQueryAsLocalStorage() {
      if (typeof window === 'undefined') return;
      localStorage.setItem('searchQuery', this.query || '');
      this.clearSearchSession();
      this.$store.commit('global/updatedSearchOpen', false);
      this.$store.commit('global/changeAllowDrawersToOpen', true);
      let targetPath = this.searchAction || `/${this.$i18n?.locale || 'en'}/search/`;
      if (typeof targetPath !== 'string') targetPath = '/search/';
      if (!targetPath.startsWith('/')) targetPath = '/' + targetPath;
      targetPath = targetPath.replace(/([^:])\/+/g, '$1/');
      const currentPath =
        (this.$route && this.$route.path) || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const samePage = this.isSameRoute(currentPath, targetPath);
      if (samePage) {
        if (this.$nuxt) this.$nuxt.$emit('search:consumePersistedQuery');
      } else if (this.$router && typeof this.$router.push === 'function') {
        this.$router.push({ path: targetPath });
      }
    },
    paginate(type, index, n) {
      switch (type) {
        case 'next':
          if (this.currentPage < this.pages) this.currentPage += 1;
          break;
        case 'previous':
          if (this.currentPage > 1) this.currentPage -= 1;
          break;
        case 'single':
          if (n !== undefined) this.currentPage = n;
          break;
        case 'first':
          this.currentPage = 1;
          break;
        case 'last':
          this.currentPage = this.pages;
          break;
      }

      this.sendQuery();

      this.$nextTick(() => {
        window.scrollTo(0, 0);
        this.$refs.searchInput && this.$refs.searchInput.focus({ preventScroll: true });
      });
    },
  },
  created() {},
  mounted() {
    if (!this.isSearchAnalyticsAllowed()) {
      this.showOneTrustBannerIfNoDecision();
    }
    this.ensureUserToken();
    if (this.searchResultsVisible && !this.searchAction) {
      if (typeof window !== 'undefined' && localStorage.getItem('searchQuery')) {
        this.consumePersistedQuery();
      } else {
        const restored = this.restoreSearchSession();
        if (!restored) {
          this.consumePersistedQuery();
        }
      }
      if (this.$nuxt) this.$nuxt.$on('search:consumePersistedQuery', this.consumePersistedQuery);
    }
  },
});
</script>

<style lang="scss">
.c_search_widget {
  .c_search_widget__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 120px;
    margin-top: 2rem;
    font-size: 1.2rem;
    color: $color_black;
    text-align: center;
    width: 100%;
  }
  .c_search_widget__no_results {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 120px;
    margin-top: 2rem;
    font-size: 1.2rem;
    color: $color_black;
    text-align: center;
    width: 100%;
  }
  position: relative;
  background: $color_white;
  padding: px_to_rem(20) var(--wrapper-padding, 5vw);
  .c_global_header & {
    width: 100%;
    max-width: 100%;
    background: $color_black;
    box-sizing: border-box;
    z-index: 99;
  }
  @at-root #{&}--hidden {
    position: absolute;
    top: 100%;
    transition:
      visibility 0s 5s,
      opacity 0.5s ease;
    opacity: 0;
    visibility: hidden;
  }
  @at-root #{&}--visible {
    opacity: 1;
    transition:
      visibility 0s 0s,
      opacity 0.5s ease;
    visibility: visible;
  }
  @at-root #{&}__form {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0;
    @include mq($mq_sm_to_md) {
      justify-content: space-between;
    }
  }
  @at-root #{&}__hidden {
    @include hide_text;
    position: absolute;
    height: 0;
    width: 0;
  }
  @at-root #{&}__content {
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    flex: 0 1 calc(100% - 72px - 1rem);
    @include mq($mq_sm_to_md) {
      flex: 0 1 calc(100% - 142px - 1rem);
    }
    input[type='search'] {
      height: px_to_rem(43);
      border-color: $color_gray_86;
      border-radius: 0;
      padding: 1rem 3rem 1rem 1rem;
      transition: all ease 300ms;
      &::placeholder {
        color: $color_black;
        opacity: 1;
      }
      .c_global_header & {
        color: $color_black;
        background: $color_white;
        border: none;
        border-bottom: solid thin $color_yellow;
        border-radius: 0;
        padding: 0 px_to_rem(12);
        width: 100%;
        &::placeholder {
          color: $color_black;
          opacity: 1;
        }
        &:focus {
          outline: solid $color_red;
        }
      }
    }
  }
  @at-root #{&}__label {
    color: $color_black;
    font-size: px_to_rem(22);
    width: 100%;
    margin-bottom: 1rem;
    .c_global_header & {
      color: $color_white;
    }
  }
  @at-root #{&}__icon {
    position: relative;
    @include mq($mq_grid_start) {
      height: 1rem !important;
      width: 1rem !important;
      margin-right: 1rem;
    }
    :hover > &,
    :focus > & {
      --stroke-color: var(--color-black) !important;
    }
    .c_global_header & {
      position: relative;
      top: auto;
      right: auto;
      transform: none;
    }
    > svg {
      overflow: visible;
    }
  }
  @at-root #{&}__submit {
    align-self: flex-end;
    width: max-content;
    padding: px_to_rem(8) px_to_rem(20);
    @include mq($mq_grid_start) {
      padding: var(--button-pt, 0.85em) var(--button-px, 20px) var(--button-pb, 0.7em);
    }
    #{&}__label {
      text-indent: -9999vw;
      @include mq($mq_grid_start) {
        text-indent: initial;
      }
    }
  }
  @at-root #{&}__pagination {
    display: flex;
    flex-wrap: wrap;
    gap: px_to_rem(6);
    justify-content: center;
    margin-top: px_to_rem(20);
    overflow-x: auto;
    max-width: 100%;

    @include mq($mq_grid_start) {
      justify-content: flex-end;
      flex-wrap: nowrap;
      overflow-x: visible;
    }

    &__indicator {
      font-size: px_to_rem(18);
      width: px_to_rem(40);
      height: px_to_rem(40);
      background: none;
      border: none;
      border-radius: 0;
      color: $color_black;
      cursor: pointer;
      padding: 0;
      margin: 0;
      text-align: center;
      line-height: px_to_rem(40);
      user-select: none;

      &--current {
        background: $color_red;
        font-weight: bold;
        text-decoration: underline;
      }
      &:hover,
      &:focus {
        background: none;
        outline: none;
        box-shadow: none;
        color: $color_black;
        text-decoration: underline;
      }
    }
    &__ellipsis {
      font-size: px_to_rem(18);
      width: px_to_rem(40);
      height: px_to_rem(40);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #a6a6a6;
      pointer-events: none;
      user-select: none;
    }
  }
}
</style>
